<template>
  <ts-loading-banner :loading="loading" class="full-page-loader">
    <div class="registration-container">
      <div class="form-wrapper">
        <div class="form-scroll">
          <div class="header-section">
            <img
              src="/assets/images/BtiLogo.png"
              alt="Event Logo"
              class="event-logo"
            />

            <div class="text-container">
              <h1 class="form-title">
                {{ $t('eventManagement.eventRegistration') }}
              </h1>
              <p class="form-subtitle">
                {{ $t('eventManagement.fillinformation') }}
              </p>
            </div>

            <div class="language-switcher">
              <button
                v-for="language in languages"
                :key="language.code"
                @click="changeLanguage(language)"
                class="language-btn"
                :class="{ active: $root.$i18n.locale === language.code }"
              >
                <span
                  :class="`flag-icon flag-icon-${language.icon}`"
                  :title="language.caption"
                ></span>
                <span class="language-text">{{ language.caption }}</span>
              </button>
            </div>
          </div>

          <form class="registration-form">
            <div class="form-group">
              <label for="name" class="form-label">
                {{ $t('eventManagement.fullName') }}
                <span class="required">*</span>
              </label>
              <input
                id="name"
                v-model="model.name"
                type="text"
                class="form-input"
                :placeholder="$t('eventManagement.enterFullName')"
                maxlength="100"
                required
                :class="{ 'is-invalid': errors.has('name') }"
              />
              <div class="invalid-feedback" v-if="errors.has('name')">
                {{ errors.first('name') }}
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">
                {{ $t('eventManagement.sex') }} <span class="required">*</span>
              </label>
              <div class="radio-group" :class="{ 'is-invalid-group': errors.has('sex') }">
                <label class="radio-label">
                  <input
                    v-model="model.sex"
                    type="radio"
                    value="Male"
                    name="sex"
                    required
                  />
                  <span class="radio-text">{{
                    $t('eventManagement.male')
                  }}</span>
                </label>
                <label class="radio-label">
                  <input
                    v-model="model.sex"
                    type="radio"
                    value="Female"
                    name="sex"
                    required
                  />
                  <span class="radio-text">{{
                    $t('eventManagement.female')
                  }}</span>
                </label>
                <label class="radio-label">
                  <input
                    v-model="model.sex"
                    type="radio"
                    value="Other"
                    name="sex"
                    required
                  />
                  <span class="radio-text">{{
                    $t('eventManagement.other')
                  }}</span>
                </label>
              </div>
              <div class="invalid-feedback" v-if="errors.has('sex')">
                {{ errors.first('sex') }}
              </div>
            </div>

            <div class="form-group">
              <label for="phone" class="form-label">
                {{ $t('eventManagement.phoneNumber') }}
                <span class="required">*</span>
              </label>
              <VueTelInput
                v-model="model.phone"
                :placeholder="$t('eventManagement.enterPhoneNumber')"
                default-country="KH"
                :input-options="{
                  showDialCode: true,
                  disabledFetchingCountry: true
                }"
                :class="{ 'is-invalid': errors.has('phone') }"
              />
              <div class="invalid-feedback" v-if="errors.has('phone')">
                {{ errors.first('phone') }}
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">{{
                $t('eventManagement.occupation')
              }}</label>
              <div class="radio-group">
                <label class="radio-label">
                  <input
                    type="radio"
                    value="Student"
                    v-model="model.occupation"
                    name="occupation"
                  />
                  <span class="radio-text">{{
                    $t('eventManagement.student')
                  }}</span>
                </label>
                <label class="radio-label">
                  <input
                    type="radio"
                    value="Other"
                    v-model="model.occupation"
                    name="occupation"
                  />
                  <span class="radio-text">{{
                    $t('eventManagement.other')
                  }}</span>
                </label>
              </div>

              <div
                v-if="model.occupation === 'Other'"
                style="margin-top: 12px;"
              >
                <input
                  type="text"
                  v-model="model.otherOccupation"
                  class="form-input"
                  :placeholder="$t('eventManagement.specifyOccupation')"
                  maxlength="100"
                />
              </div>
            </div>
          </form>

          <div class="form-actions">
            <button
              type="button"
              class="submit-btn"
              :disabled="loading"
              @click="submitForm"
            >
              <span v-if="!loading">{{
                $t('eventManagement.registerNow')
              }}</span>
              <span v-else>{{ $t('eventManagement.processing') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </ts-loading-banner>
</template>

<script>
import { Errors } from 'form-backend-validation'
import PageOptions from '@/config/PageOptions.vue'
import html2canvas from 'html2canvas'
import VueTelInput from 'vue-tel-input'
import 'vue-tel-input/dist/vue-tel-input.css'

export default {
  name: 'EventManagementRegister',
  components: {
    VueTelInput
  },
  data() {
    return {
      model: {
        name: '',
        sex: '',
        phone: '',
        email: '',
        occupation: '',
        otherOccupation: '',
        event_mgt_id: null
      },
      loading: false,
      errors: new Errors(),
      showTicketModal: false,
      ticketData: null,
      languages: [
        { code: 'kh', icon: 'kh', caption: 'ខ្មែរ' },
        { code: 'en', icon: 'us', caption: 'English' }
      ]
    }
  },
  created() {
    this.logout()
    const token = this.$route.query.token
    this.token = token
    PageOptions.pageEmpty = true
    // NOTE: This class will set the body to overflow: hidden
    document.body.className = 'bg-white fix-mobile-scroll'
    // Set default language to English
    this.$root.$i18n.locale = 'kh'
  },
  methods: {
    changeLanguage(language) {
      this.$root.$i18n.locale = language.code
    },
    submitForm() {
      this.errors = new Errors()
      this.loading = true

      const occupation =
        this.model.occupation === 'Other'
          ? this.model.otherOccupation
          : this.model.occupation

      const event_mgt_id = this.$route.query.event_mgt_id
      const payload = {
        ...this.model,
        occupation,
        event_mgt_id
      }
      this.$store
        .dispatch('eventManagementRegister/store', payload)
        .then(response => {
          if (response.message_id === -1) {
            this.$toasted.error(response.message)
            this.showTicketModal = false
            this.ticketData = null
            return
          }

          // this.$toasted.success(response.message)
          this.$emit('registration-success', response)
        })
        .catch(error => {
          // 1. Populate the errors object
          this.errors = new Errors(error.errors)

          // 2. CRITICAL FIX: Only show the generic toast if NO field errors were found
          if (!this.errors.any()) {
            this.$toasted.error(error.message || 'An unexpected error occurred.')
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    downloadTicket() {
      const ticketElement = this.$refs.ticketCard

      if (!ticketElement) {
        this.$toasted.error('Error: Could not find ticket element.')
        return
      }

      this.loading = true

      this.$nextTick(() => {
        setTimeout(() => {
          html2canvas(ticketElement, {
            scale: 2,
            useCORS: false,
            allowTaint: false,
            backgroundColor: '#ffffff',
            width: ticketElement.offsetWidth,
            height: ticketElement.offsetHeight
          })
            .then(canvas => {
              const imageURL = canvas.toDataURL('image/png')
              const a = document.createElement('a')
              a.href = imageURL

              const regNo = this.ticketData
                ? this.ticketData.registration_no
                : 'ticket'
              a.download = `${regNo}_Event_Ticket.png`

              document.body.appendChild(a)
              a.click()
              document.body.removeChild(a)

              this.$toasted.success('Ticket Downloaded Successfully')
            })
            .catch(error => {
              this.$toasted.error(
                'Failed to download ticket image. Please try again.'
              )
            })
            .finally(() => {
              this.loading = false
            })
        }, 50)
      })
    },

    closeModal() {
      this.showTicketModal = false
      this.ticketData = null
      this.clearData()
    },
    async logout() {
      await this.$store.dispatch('logout')
    },
    clearData() {
      this.model = {
        name: '',
        sex: '',
        phone: '',
        email: '',
        occupation: '',
        otherOccupation: '',
        event_mgt_id: null
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    PageOptions.pageEmpty = false
    // NOTE: We need to clean up the body class when leaving
    document.body.className = document.body.className.replace(
      ' fix-mobile-scroll',
      ''
    )
    document.body.className = document.body.className.replace('bg-white', '')
    next()
  }
}
</script>

<style>
.fix-mobile-scroll {
  height: 100vh;
  overflow: hidden;
}
</style>

<style scoped>
/* *** GENERAL LAYOUT STYLES *** */

.full-page-loader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
}

.registration-container {
  position: relative;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.form-wrapper {
  width: 100%;
  max-width: 600px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  padding-bottom: env(safe-area-inset-bottom);
  padding-bottom: constant(safe-area-inset-bottom);
}

.form-scroll {
  overflow-y: auto;
  flex: 1;
  padding: 20px 30px;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
  transform: translateZ(0);
}

.form-actions {
  padding-top: 16px;
  margin-top: 16px;
  background: white;
  border-top: 1px solid #e0e0e0;
  box-shadow: none;
  flex-shrink: 0;
}

.header-section {
  background: white;
  padding: 10px 10px;
  color: white;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 25px;
  position: relative;
}

.event-logo {
  width: 180px;
  height: 180px;
  object-fit: contain;
}

.text-container {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.form-title {
  font-size: 30px;
  font-weight: 700;
  margin: 0;
  color: rgba(213, 144, 93);
}

.form-subtitle {
  font-size: 15px;
  margin: 4px 0 0 0;
  opacity: 0.9;
  color: rgba(213, 144, 93);
}

.language-switcher {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.language-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  font-weight: 500;
  color: #666;
}

.language-btn:hover {
  border-color: #d5905d;
  background: #fff8f0;
}

.language-btn.active {
  border-color: #d5905d;
  background: #d5905d;
  color: white;
}

.language-text {
  display: none;
}

.flag-icon {
  width: 20px;
  height: 15px;
  display: inline-block;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
}

.form-group {
  margin-bottom: 24px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.required {
  color: #e74c3c;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  font-size: 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* VueTelInput custom styling to match form-input height */
.form-group >>> .vue-tel-input {
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.form-group >>> .vue-tel-input:focus-within {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-group >>> .vue-tel-input input {
  padding: 12px 16px;
  font-size: 16px;
  border: none;
  outline: none;
}

.form-group >>> .vti__dropdown {
  padding: 12px 8px;
  border-right: 1px solid #e0e0e0;
}

.form-group >>> .vti__dropdown:hover {
  background-color: #f8f9fa;
}

.form-group >>> .vti__dropdown.open {
  background-color: #f8f9fa;
}

.radio-group {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.radio-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.radio-label input[type='radio'] {
  width: 20px;
  height: 20px;
  margin-right: 8px;
  cursor: pointer;
  accent-color: #667eea;
}

.radio-text {
  font-size: 16px;
  color: #555;
}

.submit-btn {
  width: 100%;
  padding: 14px 24px;
  font-size: 18px;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, #d5905d 0%, #b67c4b 100%);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(213, 144, 93, 0.4);
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(213, 144, 93, 0.5);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}


/* *** INPUT VALIDATION STYLES (UNIVERSAL FIX) *** */

/* 1. Makes the input border red (for 'name' and generic inputs) */
.form-input.is-invalid {
  border-color: #e74c3c !important; 
}

/* 2. Styles the error message text */
.invalid-feedback {
  display: block; 
  color: #e74c3c; 
  font-size: 13px;
  margin-top: 4px; 
}

/* 3. Styles the radio group container (for 'sex') */
.form-group .radio-group.is-invalid-group {
    /* Ensuring this border overrides all others for validation visibility */
    border: 2px solid #e74c3c !important; 
    border-radius: 8px;
    padding: 8px;
    margin-top: 4px;
}

/* 4. Styles VueTelInput component border (for 'phone') */
.form-group >>> .vue-tel-input.is-invalid {
    border-color: #e74c3c !important;
}

.form-group >>> .vue-tel-input.is-invalid:focus-within {
    border-color: #e74c3c !important;
    box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.1); 
}


/* Mobile Responsive */
@media (max-width: 768px) {
  .language-btn .language-text {
    display: inline;
  }
}

@media (max-width: 480px) {
  .header-section {
    flex-direction: column;
    text-align: center;
  }

  .event-logo {
    width: 200px;
    height: 200px;
  }

  .form-title {
    font-size: 26px;
  }

  .form-subtitle {
    font-size: 14px;
  }

  .language-switcher {
    width: 100%;
    justify-content: center;
  }

  .language-btn {
    flex: 1;
    justify-content: center;
  }
}
</style>
